import type { ICUParams } from "@v0y4ge/i18n-runtime";
import { currentLocale } from "../../lib/i18n";
import type { ClientTierCode, MatchFactorCode, MatchReason, MatchReasonCode, ServiceCategoryCode, SoMoney } from "./so-api";

type Translate = (key: string, params?: ICUParams) => string;

/* Every dynamic catalog key is built from a SERVED code. A payload that drifts
 * (or a code the catalog hasn't learned yet) must degrade to a translated
 * placeholder — never echo `<namespace>.undefined` onto the screen, which is
 * exactly what the pre-coded floor payload did (operator report 2026-08-05). */
const SERVICE_CATEGORY_CODES: ReadonlySet<string> = new Set<ServiceCategoryCode>([ // ui-config
  "styling",
  "leather-goods",
  "jewellery",
  "private-client",
  "beauty",
]);
const CLIENT_TIER_CODES: ReadonlySet<string> = new Set<ClientTierCode>(["select", "gold", "icon"]);
const MATCH_REASON_CODES: ReadonlySet<string> = new Set<MatchReasonCode>([
  "prior_purchase",
  "requested_advisor",
  "language_tier_fit",
  "highest_ltv",
  "continuing_consultation",
  "direct_booking",
  "reassigned_advisor",
  "no_language_specialist",
  "no_certified_advisor",
  "specialist_absent",
]);
const MATCH_FACTOR_CODES: ReadonlySet<string> = new Set<MatchFactorCode>([
  "client_book",
  "language",
  "category",
  "availability",
  "certification",
  "history",
  "best_option",
  "partial",
  "relationship",
  "specialism",
  "reassigned",
]);

export const serviceCategoryKey = (code: ServiceCategoryCode): `serviceCategory.${ServiceCategoryCode}` =>
  `serviceCategory.${code}`;

export const clientTierKey = (code: ClientTierCode): `clientTier.${ClientTierCode}` =>
  `clientTier.${code}`;

export const matchReasonKey = (code: MatchReason["code"]): `matchReason.${MatchReason["code"]}` =>
  `matchReason.${code}`;

export const matchFactorKey = (code: MatchFactorCode): `matchFactor.${MatchFactorCode}` =>
  `matchFactor.${code}`;

/** Translated service-category name, or the "uncategorised" placeholder. */
export function serviceCategoryLabel(code: ServiceCategoryCode | null | undefined, t: Translate): string {
  return code && SERVICE_CATEGORY_CODES.has(code)
    ? t(serviceCategoryKey(code))
    : t("storeops.vocab.uncategorised");
}

/** Translated tier name, or the "unranked" placeholder. */
export function clientTierLabel(code: ClientTierCode | null | undefined, t: Translate): string {
  return code && CLIENT_TIER_CODES.has(code) ? t(clientTierKey(code)) : t("storeops.vocab.untiered");
}

/** Translated match-factor label, or the "other" placeholder. */
export function matchFactorLabel(code: MatchFactorCode | null | undefined, t: Translate): string {
  return code && MATCH_FACTOR_CODES.has(code) ? t(matchFactorKey(code)) : t("storeops.vocab.otherFactor");
}

/** Match rationale. Unknown codes render nothing rather than a raw key. */
export function renderMatchReason(reason: MatchReason | null | undefined, t: Translate): string {
  if (!reason || !MATCH_REASON_CODES.has(reason.code)) return "";
  const params: ICUParams = { ...reason.params };
  if (typeof params.category === "string" && SERVICE_CATEGORY_CODES.has(params.category)) {
    params.category = t(serviceCategoryKey(params.category as ServiceCategoryCode));
  }
  return t(matchReasonKey(reason.code), params);
}

/** The need line under a client name: category, plus the client's own language. */
export function renderNeedLine(code: ServiceCategoryCode | null | undefined, langs: string | null | undefined, t: Translate): string {
  const category = serviceCategoryLabel(code, t);
  const lang = primaryNonEnglishLang(langs);
  return lang ? t("storeops.storeFloor.needLine.icu", { category, language: lang }) : category;
}

/* Language names arrive as free text on the client record ("Italian (native) ·
 * English"). The need line shows a locale-NEUTRAL ISO code, never the English
 * name — an unmapped language is simply omitted rather than printed in English. */
const LANG_CODE: Record<string, string> = { // ui-config
  english: "EN", french: "FR", german: "DE", italian: "IT", spanish: "ES", portuguese: "PT",
  japanese: "JA", mandarin: "ZH", cantonese: "ZH", chinese: "ZH", korean: "KO", hindi: "HI",
  punjabi: "PA", arabic: "AR", dutch: "NL", czech: "CS", polish: "PL", russian: "RU", turkish: "TR",
};

/** "Italian (native) · English" → "IT"; English-only or unmapped → null. */
export function primaryNonEnglishLang(langs: string | null | undefined): string | null {
  const parts = (langs || "")
    .split(/[,/·]/)
    .map((x) => x.trim().replace(/\s*\(.*\)\s*$/, ""))
    .filter(Boolean);
  for (const p of parts) {
    const code = p.length <= 3 ? p.toUpperCase() : LANG_CODE[p.toLowerCase()];
    if (code && code !== "EN") return code;
  }
  return null;
}

const intlLocale = (locale = currentLocale()): string => (locale === "en" ? "en-GB" : locale);

const money = (m: SoMoney, opts: Intl.NumberFormatOptions, locale: string): string => {
  try {
    return new Intl.NumberFormat(intlLocale(locale), { style: "currency", currency: m.currencyCode, currencyDisplay: "narrowSymbol", ...opts }).format(m.amount);
  } catch {
    return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: opts.maximumFractionDigits ?? 0 }).format(m.amount);
  }
};

/** Compact lifetime spend for dense rows — "£142K". Null → em dash. */
export function formatSpend(spend: SoMoney | null | undefined, locale = currentLocale()): string {
  if (!spend || typeof spend.amount !== "number" || Number.isNaN(spend.amount)) return "—";
  return money(spend, { notation: "compact", maximumFractionDigits: 1, minimumFractionDigits: 0 }, locale);
}

/** Exact money for the client-360 stats — "£142,000". Null → em dash. */
export function formatMoney(amount: SoMoney | null | undefined, locale = currentLocale()): string {
  if (!amount || typeof amount.amount !== "number" || Number.isNaN(amount.amount)) return "—";
  return money(amount, { maximumFractionDigits: 0 }, locale);
}

/** Revenue reported in thousands (kpis.mtdK, region mtdK/targetK) — "£1.28M". */
export function formatMoneyK(amountK: number | null | undefined, currencyCode: string, locale = currentLocale()): string {
  if (typeof amountK !== "number" || Number.isNaN(amountK)) return "—";
  return money({ amount: amountK * 1000, currencyCode }, { notation: "compact", maximumFractionDigits: 2 }, locale);
}

/** Store-day headline date — "Saturday, 12 July". */
export function formatFloorDate(iso: string | null | undefined, locale = currentLocale()): string {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), { weekday: "long", day: "numeric", month: "long" }).format(d);
  } catch {
    return iso ?? "";
  }
}

/** Weekday alone — the demand chart's title. */
export function formatWeekday(iso: string | null | undefined, locale = currentLocale()): string {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), { weekday: "long" }).format(d);
  } catch {
    return "";
  }
}

/** Month-and-year label for the region period ("2026-07" → "July 2026"). */
export function formatPeriod(iso: string | null | undefined, locale = currentLocale()): string {
  const d = iso ? new Date(`${iso}-01T00:00:00`) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), { month: "long", year: "numeric" }).format(d);
  } catch {
    return iso ?? "";
  }
}

/** Wall-clock time for the coverage card's "Now ·" stamp. */
export function formatClock(date = new Date(), locale = currentLocale()): string {
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), { hour: "2-digit", minute: "2-digit" }).format(date);
  } catch {
    return "";
  }
}
